<template>
  <div class="charging-point-diagnostics">
    <v-card outlined @click="open = true">
      <v-card-text class="d-flex align-center" style="gap: 12px">
        <v-icon>mdi-file-search-outline</v-icon>
        <div class="flex-grow-1">
          <div class="text-subtitle-1 font-weight-medium text--primary">{{ $t('firmware.diagnostic.title') }}</div>
          <div class="text-caption text--secondary">
            <template v-if="pending">{{ $t('firmware.diagnostic.statuses.' + pending.status) }}</template>
            <template v-else-if="lastReceived">{{ $t('firmware.diagnostic.last-received', {date: formatDate(lastReceived.receivedAt)}) }}</template>
            <template v-else>{{ $t('firmware.diagnostic.none-yet') }}</template>
          </div>
        </div>
        <v-progress-circular v-if="pending" indeterminate size="16" width="2" color="primary" />
        <v-icon>mdi-chevron-right</v-icon>
      </v-card-text>
    </v-card>

    <v-dialog v-model="open" max-width="1000" scrollable>
      <v-card v-if="open">
        <!-- En-tête : la borne, et l'état de ses points de charge en direct (une borne se met en indisponible pendant
             qu'elle prépare ses journaux : on le voit sans quitter la fenêtre) -->
        <v-card-title class="d-flex align-center flex-wrap" style="gap: 12px">
          <div class="flex-grow-1">
            <div class="text-overline text--secondary" style="line-height: 1.4">{{ $t('firmware.diagnostic.title') }}</div>
            <div class="text-h6" style="line-height: 1.3">{{ chargingPoint.name || chargingPoint.identifier }}</div>
          </div>
          <div v-if="chargingPoint.evses && chargingPoint.evses.length" class="d-flex align-center flex-wrap live-status" style="gap: 6px">
            <span class="text-caption text--secondary">{{ $t('firmware.diagnostic.live-status') }}</span>
            <chip-status
                v-for="(evse, i) in chargingPoint.evses" :key="`evse-${i}`" small
                :value="evse.status"
                :charging-point-id="chargingPoint.id"
                :connector-ids="evse.connectors.map(c => c.connectorId)"
                :title="evse.internalId"
            />
          </div>
          <v-btn icon small :loading="loading" :title="$t('refresh')" @click="load()"><v-icon small>mdi-refresh</v-icon></v-btn>
          <v-btn icon small :aria-label="$t('close')" @click="open = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-4">
          <!-- Demander : la période en option, le moyen de dépôt vient du constructeur -->
          <div class="mb-3 d-flex align-center flex-wrap" style="gap: 12px; min-height: 40px">
            <v-btn small color="primary" depressed class="compact" :loading="sending" :disabled="!!pending" @click="send">
              <v-icon small left>mdi-file-search-outline</v-icon>{{ $t('firmware.diagnostic.request') }}
            </v-btn>
            <template v-if="!withPeriod">
              <span class="text-body-2">{{ $t('firmware.diagnostic.all-logs') }}</span>
              <a class="text-body-2" @click="withPeriod = true">{{ $t('firmware.diagnostic.choose-period') }}</a>
            </template>
            <template v-else>
              <v-text-field v-model="startTime" :label="$t('firmware.diagnostic.start-time')" type="datetime-local" clearable outlined dense hide-details style="max-width: 240px" />
              <v-text-field v-model="stopTime" :label="$t('firmware.diagnostic.stop-time')" type="datetime-local" clearable outlined dense hide-details style="max-width: 240px" />
              <a class="text-body-2" @click="clearPeriod">{{ $t('firmware.diagnostic.all-logs') }}</a>
            </template>
            <v-spacer />
            <span v-if="pending" class="text-caption text--secondary">
              <v-icon x-small>mdi-autorenew</v-icon> {{ $t('firmware.diagnostic.auto-refresh') }}
            </span>
          </div>
          <v-divider />

          <firmware-diagnostics-table :items="items" :loading="loading && !items.length" @downloaded="load()" />

          <p class="text-caption text--secondary mt-3 mb-0">
            <v-icon x-small>mdi-shield-account-outline</v-icon> {{ $t('firmware.diagnostic.explained') }}
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FirmwareRepository from "@repository/FirmwareRepository";
import FirmwareDiagnosticsTable from "@pages/Firmware/FirmwareDiagnosticsTable.vue";
import {EventBus} from "@includes/EventBus";

/** Toutes les 10 secondes tant qu'une demande attend la borne : c'est le job (chaque minute) qui la fait avancer. */
const REFRESH_MS = 10000

/**
 * Les diagnostics d'une borne, dans l'onglet Gestion : un bouton qui dit où en est le dernier, et la fenêtre pour en
 * demander un (toute la période par défaut) et télécharger ceux reçus. Le protocole de dépôt n'est jamais demandé : il
 * vient des réglages du constructeur de la borne. La fenêtre se tient à jour seule tant qu'une demande est en cours.
 */
export default {
  components: {FirmwareDiagnosticsTable},

  props: {
    chargingPoint: {type: Object, required: true},
  },

  data() {
    return {
      items: [],
      loading: false,
      open: false,
      sending: false,
      withPeriod: false,
      startTime: '',
      stopTime: '',
      listener: null,
      timer: null,
    }
  },

  computed: {
    // Une demande qui attend encore la borne : on n'en lance pas une deuxième.
    pending() {
      return this.items.find(item => item.open) || null
    },
    lastReceived() {
      return this.items.find(item => item.receivedAt) || null
    },
  },

  watch: {
    pending(value) {
      this.schedule(!!value)
    },
    open(value) {
      if (value) this.load()
    },
  },

  mounted() {
    this.load()
    // Le statut de dépôt reçu de la borne fait avancer la demande : on relit (et encore après le passage du job).
    this.listener = EventBus.$on('on-DiagnosticsStatusNotification-' + this.chargingPoint.id, () => this.load())
  },

  beforeDestroy() {
    this.schedule(false)
    if (this.listener) {
      EventBus.$off(this.listener)
    }
  },

  methods: {
    load() {
      this.loading = true
      return FirmwareRepository.diagnostics(this.chargingPoint.id).then(items => {
        this.items = items
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    schedule(active) {
      if (active && !this.timer) {
        this.timer = setInterval(() => this.load(), REFRESH_MS)
      } else if (!active && this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    clearPeriod() {
      this.withPeriod = false
      this.startTime = ''
      this.stopTime = ''
    },

    send() {
      this.sending = true
      // datetime-local : heure locale du navigateur, convertie en date complète avec fuseau.
      const iso = value => value ? new Date(value).toISOString() : null
      FirmwareRepository.requestDiagnostic(this.chargingPoint.id, {
        startTime: iso(this.startTime),
        stopTime: iso(this.stopTime),
      }).then(() => {
        this.notifySuccess('firmware.diagnostic.requested')
        this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.sending = false
      })
    },
  },
}
</script>

<style scoped>
.compact >>> .v-icon--left {
  margin-right: 6px !important;
}
.live-status >>> .chip-status {
  height: 22px !important;
  font-size: 12px !important;
  padding: 0 10px !important;
}
.live-status >>> .chip-status .v-icon {
  font-size: 14px !important;
}
</style>
